import { CommonModule } from "@angular/common";
import { BreakpointObserver } from "@angular/cdk/layout";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { NavigationStart, Router } from "@angular/router";
import { filter, Subscription } from "rxjs";
import {
  VngBadgeComponent,
  VngIconButtonComponent,
  VngIconComponent,
} from "@vengreso/angular-components";

import { AppNotificationService } from "src/app/core/core-services/services/app-notification.service";
import { NotificationPanelComponent } from "../notification-panel/notification-panel.component";

/** Below this viewport width the panel goes full-height instead of a fixed-width popover. */
const MOBILE_BREAKPOINT = "(max-width: 640px)";

/**
 * The notification center's entry point: bell icon + unread badge in the
 * app header, opening `NotificationPanelComponent` in a CDK Overlay drawer.
 * The panel is recreated for each open, so it refreshes page 1 each time;
 * the notification service retains the realtime connection and unread count
 * for the lifetime of the bell.
 *
 * ONLY mounted for beta users — `header.component.html` wraps this in
 * `*ngIf="showNotificationCenter"` (`userHasNotificationCenterAccess`), so
 * for everyone else this component, and everything it would start, simply
 * never exists in the DOM. `AppNotificationService.init()`/`teardown()` are
 * also individually gated (defense in depth — see that service's JSDoc),
 * but in practice a non-beta user never reaches either call.
 */
@Component({
  selector: "app-notification-bell",
  standalone: true,
  imports: [
    CommonModule,
    VngBadgeComponent,
    VngIconButtonComponent,
    VngIconComponent,
  ],
  templateUrl: "./notification-bell.component.html",
  styleUrls: ["./notification-bell.component.scss"],
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  unreadCount$ = this.appNotificationService.unreadCount$;
  isOpen = false;

  private overlayRef: OverlayRef | null = null;
  private drawerSubscriptions = new Subscription();
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly appNotificationService: AppNotificationService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly overlay: Overlay,
    private readonly router: Router
  ) {}

  unreadLabel(count: number): string {
    return count > 99 ? "99+" : String(count);
  }

  ngOnInit(): void {
    this.appNotificationService.init();
  }

  ngOnDestroy(): void {
    this.closeDrawer(true);
    this.appNotificationService.teardown();
  }

  toggleDrawer(): void {
    if (this.isOpen) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  private openDrawer(): void {
    if (this.overlayRef || this.closeTimer) {
      return;
    }

    const isMobile = this.breakpointObserver.isMatched(MOBILE_BREAKPOINT);
    const overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: "fms-notification-drawer-backdrop",
      panelClass: "fms-notification-drawer",
      positionStrategy: this.overlay.position().global().top("0").right("0"),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: isMobile ? "100vw" : "400px",
      height: "100vh",
    });
    this.overlayRef = overlayRef;
    overlayRef.attach(new ComponentPortal(NotificationPanelComponent));
    this.isOpen = true;

    requestAnimationFrame(() => {
      if (this.overlayRef === overlayRef && this.isOpen) {
        overlayRef.overlayElement.classList.add("fms-notification-drawer--open");
      }
    });

    this.drawerSubscriptions = new Subscription();
    this.drawerSubscriptions.add(overlayRef.backdropClick().subscribe(() => this.closeDrawer()));
    this.drawerSubscriptions.add(
      overlayRef
        .keydownEvents()
        .pipe(filter((event) => event.key === "Escape"))
        .subscribe(() => this.closeDrawer())
    );
    this.drawerSubscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationStart))
        .subscribe(() => queueMicrotask(() => this.closeDrawer()))
    );
  }

  private closeDrawer(immediately = false): void {
    const ref = this.overlayRef;
    if (!ref) {
      return;
    }

    this.drawerSubscriptions.unsubscribe();
    this.drawerSubscriptions = new Subscription();
    this.isOpen = false;

    if (immediately) {
      this.disposeDrawer(ref);
      return;
    }

    ref.overlayElement.classList.remove("fms-notification-drawer--open");
    ref.overlayElement.classList.add("fms-notification-drawer--closing");
    this.closeTimer = setTimeout(() => this.disposeDrawer(ref), 220);
  }

  private disposeDrawer(ref: OverlayRef): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    ref.dispose();
    if (this.overlayRef === ref) {
      this.overlayRef = null;
    }
  }
}
